interface NamePattern {
  pattern: RegExp;
  description: string;
}

interface ValidationRule {
  validate: (name: string) => boolean;
  description: string;
}

/**
 * Configuration object for name extraction patterns
 */
const NAME_PATTERNS: NamePattern[] = [
  {
    pattern: /(?:resume|cv)\s*[-_]?\s*([a-z\s]+)/i,
    description: "Resume/CV prefix pattern (e.g., 'Resume - John Doe')",
  },
  {
    pattern: /^([a-z]+(?:\s[a-z]+)?(?:\s[a-z]+)?)\s*(?:resume|cv)/i,
    description: "Name prefix pattern (e.g., 'JohnDoe_Resume')",
  },
  {
    pattern: /(?:\d{4}[-_])?([a-z]+(?:[-_\s][a-z]+){1,2})/i,
    description: "Year prefix pattern (e.g., '2024_John_Doe')",
  },
  {
    pattern: /([a-z]+[-_\s][a-z]+)[-_\s](?:application|resume|cv)/i,
    description: "Name with suffix pattern (e.g., 'Doe_John_Application')",
  },
  {
    pattern: /^([a-z]+(?:[-_\s][a-z]+){1,2})$/i,
    description: "Simple name pattern (e.g., 'John Doe')",
  },
];

/**
 * Configuration object for name validation rules
 */
const VALIDATION_RULES: ValidationRule[] = [
  {
    validate: (name: string) => name.split(' ').length >= 2,
    description: 'Must contain at least two words',
  },
  {
    validate: (name: string) => name.split(' ').every(word => word.length >= 2),
    description: 'Each word must be at least 2 characters',
  },
  {
    validate: (name: string) => !/\d/.test(name),
    description: 'Must not contain numbers',
  },
  {
    validate: (name: string) => name.length <= 50,
    description: 'Must not exceed 50 characters',
  },
  {
    validate: (name: string) => !/(?:resume|cv|application|document|file)/i.test(name),
    description: 'Must not contain file-related keywords',
  },
];

/**
 * Result interface for name extraction
 */
interface NameExtractionResult {
  name: string | null;
  confidence: number;
  pattern?: string;
  validationErrors: string[];
}

/**
 * Validates if the extracted string looks like a proper name
 * @param name - The name to validate
 * @returns Object containing validation result and any error messages
 */
function validateName(name: string): { isValid: boolean; errors: string[] } {
  const errors: string[] = [];

  for (const rule of VALIDATION_RULES) {
    if (!rule.validate(name)) {
      errors.push(rule.description);
    }
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
}

/**
 * Formats a name string with proper capitalization
 * @param name - The name to format
 * @returns The formatted name
 */
function formatName(name: string): string {
  return name
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

/**
 * Extracts a candidate's name from a filename using various pattern matching strategies
 * @param filename - The filename to extract the name from
 * @param options - Optional configuration for the extraction process
 * @returns Object containing the extracted name and metadata
 */
export function extractCandidateName(
  filename: string,
  options: {
    minimumConfidence?: number;
    debug?: boolean;
  } = {},
): NameExtractionResult {
  const { minimumConfidence = 0.5, debug = false } = options;

  if (!filename || typeof filename !== 'string') {
    return {
      name: null,
      confidence: 0,
      validationErrors: ['Invalid filename provided'],
    };
  }

  // Clean the filename
  const cleanName = filename
    .replace(/\.[^/.]+$/, '')
    .replace(/[-_]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  let bestMatch: NameExtractionResult = {
    name: null,
    confidence: 0,
    validationErrors: [],
  };

  // Try each pattern
  for (const { pattern, description } of NAME_PATTERNS) {
    const match = cleanName.match(pattern);
    if (match && match[1]) {
      const extractedName = formatName(match[1].trim());
      const { isValid, errors } = validateName(extractedName);

      // Calculate confidence based on validation rules and pattern matching
      const confidence = isValid
        ? (1 - errors.length / VALIDATION_RULES.length) * (match[0].length / cleanName.length)
        : 0;

      if (confidence > bestMatch.confidence && confidence >= minimumConfidence) {
        bestMatch = {
          name: extractedName,
          confidence,
          pattern: description,
          validationErrors: errors,
        };
      }
    }
  }

  return bestMatch;
}


export function extractCandidateFirstName(filename: string): string {
  const result = extractCandidateName(filename);
  return result.name?.split(' ')[0] ?? '';
}

export function extractCandidateLastName(filename: string): string {
  const result = extractCandidateName(filename);
  return result.name?.split(' ')[1] ?? '';
}

